import { products, categories } from '../data/products';
import { isRedirectShortlinkPath } from './redirectShortlink';

const NOINDEX_EXACT = new Set([
  '/checkout',
  '/account',
  '/signin',
  '/wishlist',
  '/orders',
  '/forgot-password',
]);

const DEFAULT_OG_IMAGE =
  'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200';

export function getSiteOrigin() {
  if (typeof window === 'undefined') return 'https://myfashiondeals.online';
  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  return 'https://myfashiondeals.online';
}

/** Canonical path without tracking params (UTMs kept out of canonical). */
export function getCanonicalUrl(pathname = '/') {
  const origin = getSiteOrigin();
  const path = pathname === '' ? '/' : pathname;
  return `${origin}${path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path}`;
}

export function shouldNoIndex(pathname) {
  const p = (pathname || '/').replace(/\/$/, '') || '/';
  if (NOINDEX_EXACT.has(p)) return true;
  if (isRedirectShortlinkPath(pathname)) return true;
  return false;
}

function titleCase(slug = '') {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Build SEO payload for the current route.
 * Designed for organic indexing + Google Ads landing-page clarity.
 */
export function resolveSeoForLocation(pathname, search = '', siteMeta = {}) {
  const siteName = siteMeta.siteName || 'myfashiondeals.online';
  const tagline = siteMeta.tagline || 'Dress the day. Move further.';
  const path = pathname || '/';
  const params = new URLSearchParams(search || '');
  const noindex = shouldNoIndex(path);
  const canonical = getCanonicalUrl(path === '/' ? '/' : path.replace(/\/$/, '') || '/');

  const base = {
    title: `${siteName} | Women, Men & Activewear Online Store`,
    description: `Shop women, men, activewear, home, shoes & beauty at ${siteName}. Free shipping on orders $75+. Easy 30-day returns. ${tagline}`,
    image: DEFAULT_OG_IMAGE,
    type: 'website',
    robots: noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    canonical,
    jsonLd: null,
  };

  if (path === '/' || path === '') {
    return {
      ...base,
      title: `${siteName} | Shop Women, Men, Activewear & Home`,
      description: `${siteName} — ${tagline} Browse women, men, activewear, kids, home, shoes, bags, jewelry & beauty. Free shipping $75+. Transparent shipping, returns & contact.`,
    };
  }

  if (path === '/shop' || path === '/sale') {
    const q = params.get('search');
    const brand = params.get('brand');
    const isSale = path === '/sale';
    let title = isSale
      ? `Sale & Clearance | ${siteName}`
      : `Shop All Products | ${siteName}`;
    let description = isSale
      ? `Shop clearance and sale styles at ${siteName}. Markdowns on women, men, activewear and home. Free shipping on orders $75+.`
      : `Browse the full ${siteName} catalog — women, men, activewear, home, shoes and more. Free shipping $75+.`;
    if (q) {
      title = `Search “${q}” | ${siteName}`;
      description = `Results for “${q}” at ${siteName}. Shop matching styles with free shipping on orders $75+.`;
    }
    if (brand) {
      title = `${brand} | ${siteName}`;
      description = `Shop ${brand} at ${siteName}. Authentic styles with easy returns.`;
    }
    return { ...base, title, description };
  }

  const productMatch = path.match(/^\/product\/([^/]+)\/?$/);
  if (productMatch) {
    const product = products.find((p) => p.id === productMatch[1]);
    if (product) {
      const price = product.salePrice || product.price;
      const title = `${product.name} | ${product.brand} | ${siteName}`;
      const description = `${product.description} Buy ${product.name} from ${product.brand} at ${siteName}. $${Number(price).toFixed(2)}. Free shipping on orders $75+.`;
      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        image: [product.image],
        description: product.description,
        sku: String(product.id),
        brand: { '@type': 'Brand', name: product.brand },
        category: product.category,
        offers: {
          '@type': 'Offer',
          url: canonical,
          priceCurrency: 'USD',
          price: Number(price).toFixed(2),
          availability: product.inStock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@type': 'Organization', name: siteName },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: String(product.rating),
          reviewCount: String(product.reviews),
        },
      };
      return {
        ...base,
        title,
        description,
        image: product.image,
        type: 'product',
        jsonLd,
      };
    }
    return {
      ...base,
      title: `Product | ${siteName}`,
      robots: 'noindex, follow',
    };
  }

  const categoryMatch = path.match(/^\/category\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (categoryMatch) {
    const catId = categoryMatch[1];
    const sub = categoryMatch[2];
    const cat = categories.find((c) => c.id === catId);
    const catName = cat?.name || titleCase(catId);
    const subName = sub ? titleCase(sub) : '';
    const title = subName
      ? `${subName} ${catName} | ${siteName}`
      : `${catName} Shop | ${siteName}`;
    const description = subName
      ? `Shop ${subName.toLowerCase()} in ${catName} at ${siteName}. Free shipping on orders $75+. Easy 30-day returns.`
      : `Shop ${catName} at ${siteName} — curated styles with free shipping on orders $75+. ${tagline}`;
    return {
      ...base,
      title,
      description,
      image: cat?.image || DEFAULT_OG_IMAGE,
    };
  }

  const staticPages = {
    '/about': {
      title: `About ${siteName} | Our Story`,
      description: `Learn about ${siteName} — clothes and essentials for real weeks. Women, men, activewear, home and accessories. Designed with fewer SKUs and better make.`,
    },
    '/contact': {
      title: `Contact ${siteName} | Customer Service`,
      description: `Contact ${siteName} customer service. Email hello@myfashiondeals.online or call 1-888-847-8200. We're here for orders, shipping, and product questions.`,
    },
    '/faqs': {
      title: `FAQs | ${siteName}`,
      description: `Frequently asked questions about orders, shipping, returns, and accounts at ${siteName}.`,
    },
    '/shipping': {
      title: `Shipping Policy | ${siteName}`,
      description: `Shipping rates and delivery times at ${siteName}. Free standard shipping on orders $75+.`,
    },
    '/returns': {
      title: `Returns Policy | ${siteName}`,
      description: `Easy 30-day returns at ${siteName}. Learn how to start a return or exchange.`,
    },
    '/privacy': {
      title: `Privacy Policy | ${siteName}`,
      description: `How ${siteName} collects, uses, and protects your personal information.`,
    },
    '/terms': {
      title: `Terms of Use | ${siteName}`,
      description: `Terms of use for shopping at ${siteName}.`,
    },
    '/stores': {
      title: `Stores | ${siteName}`,
      description: `Visit the ${siteName} flagship at 88 Howard Street, San Francisco, or shop online anytime.`,
    },
    '/rewards': {
      title: `Rewards Program | ${siteName}`,
      description: `Earn rewards on every purchase at ${siteName}. Member pricing and early drops.`,
    },
    '/careers': {
      title: `Careers | ${siteName}`,
      description: `Join the ${siteName} team. Career openings for makers, designers, and operators.`,
    },
    '/press': {
      title: `Press | ${siteName}`,
      description: `Media inquiries for ${siteName}. Contact us for lookbooks and press resources.`,
    },
    '/gift-cards': {
      title: `Gift Cards | ${siteName}`,
      description: `${siteName} gift cards — coming soon. Share a favorite style from the catalog today.`,
    },
    '/coupons': {
      title: `Offers & Coupons | ${siteName}`,
      description: `Current promotions and member offers at ${siteName}.`,
    },
    '/signin': {
      title: `Sign In | ${siteName}`,
      description: `Sign in to your ${siteName} account.`,
    },
    '/checkout': {
      title: `Checkout | ${siteName}`,
      description: `Secure checkout at ${siteName}.`,
    },
    '/account': {
      title: `My Account | ${siteName}`,
      description: `Manage your ${siteName} account.`,
    },
    '/wishlist': {
      title: `Wishlist | ${siteName}`,
      description: `Your saved styles at ${siteName}.`,
    },
  };

  const normalized = path.replace(/\/$/, '') || '/';
  if (staticPages[normalized]) {
    return { ...base, ...staticPages[normalized] };
  }

  if (noindex) {
    return {
      ...base,
      title: siteName,
      description: tagline,
      robots: 'noindex, nofollow',
    };
  }

  return base;
}

export function upsertMetaByName(name, content) {
  if (content == null) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function upsertMetaByProperty(property, content) {
  if (content == null) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function upsertLinkRel(rel, href, id) {
  let el = id ? document.getElementById(id) : document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (id) el.id = id;
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applySeo(seo) {
  if (!seo) return;
  document.title = seo.title;
  upsertMetaByName('description', seo.description);
  upsertMetaByName('robots', seo.robots);
  upsertMetaByName('googlebot', seo.robots);
  upsertLinkRel('canonical', seo.canonical, 'seo-canonical');

  upsertMetaByProperty('og:type', seo.type || 'website');
  upsertMetaByProperty('og:url', seo.canonical);
  upsertMetaByProperty('og:title', seo.title);
  upsertMetaByProperty('og:description', seo.description);
  upsertMetaByProperty('og:image', seo.image || DEFAULT_OG_IMAGE);
  upsertMetaByProperty('og:site_name', 'myfashiondeals.online');

  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:title', seo.title);
  upsertMetaByName('twitter:description', seo.description);
  upsertMetaByName('twitter:image', seo.image || DEFAULT_OG_IMAGE);

  upsertJsonLd('seo-jsonld-page', seo.jsonLd);
}
